import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { checkoutService, CheckoutApiError } from '../services/checkout';
import { setSentryUser, updateSentryUserContext } from '../config/sentry';
import { createLogger } from '../utils/logger';
import { formatCurrency } from '../utils/format';
import type { Checkout } from '../types/checkout';
import { format, setHours } from 'date-fns';

const logger = createLogger('PaymentStatusPage');

export interface PaymentData {
  transaction: {
    id: string;
    workspace_id: string;
    type: string;
    category_id: string | null;
    cost_center_id: null;
    contact_id: string;
    bank_account_id: string;
    destination_account_id: string | null;
    amount: number;
    description: string;
    notes: string;
    reference_number: string;
    due_date: string;
    payment_date: string | null;
    status: string;
    payment_method: string;
    installments: string | null;
    installment_number: string | null;
    parent_transaction_id: null;
    tags: string[];
    is_recurring: boolean;
    recurring_config: Record<string, unknown>;
    created_at: string;
    updated_at: string;
    created_by: string | null;
    updated_by: string | null;
    parent_recurring_id: string | null;
    deleted_at: string | null;
    deleted_by: string | null;
    integration_id: string | null;
    is_scheduled: boolean;
    asaas_payment_id: string;
    contacts: {
      id: string;
      name: string;
      email: string;
      phone: string;
      document: string;
      contact_type: string;
      asaas_customer_id: string;
    };
  };
  asaas_payment: {
    object: string;
    id: string;
    dateCreated: string;
    customer: string;
    checkoutSession: string | null;
    paymentLink: string | null;
    value: number;
    netValue: number;
    originalValue: number | null;
    interestValue: number | null;
    description: string;
    billingType: string;
    canBePaidAfterDueDate: boolean;
    pixTransaction: string | null;
    status: string;
    dueDate: string;
    originalDueDate: string;
    paymentDate: string | null;
    clientPaymentDate: string | null;
    installmentNumber: string | null;
    invoiceUrl: string;
    invoiceNumber: string;
    externalReference: string;
    deleted: boolean;
    anticipated: boolean;
    anticipable: boolean;
    creditDate: string | null;
    estimatedCreditDate: string | null;
    transactionReceiptUrl: string | null;
    nossoNumero: string;
    bankSlipUrl: string;
    lastInvoiceViewedDate: string | null;
    lastBankSlipViewedDate: string | null;
    discount: {
      value: number;
      limitDate: null;
      dueDateLimitDays: number;
      type: string;
    };
    fine: {
      value: 0;
      type: string;
    };
    interest: {
      value: number;
      type: string;
    };
    postalService: boolean;
    escrow: string | null;
    refunds: string | null;
    installmentCount: number;
    paymentValue: number;
  };
  contact: {
    id: string;
    name: string;
    email: string;
    phone: string;
    document: string;
    contact_type: string;
    asaas_customer_id: string;
  };
  payment_url: string;
  pix_qr_code: {
    encodedImage: string;
    expirationDate: string;
    payload: string;
  };
  billing_type: string;
  boleto_info: {
    digitableLine: string;
    barcode: string;
    bankSlipUrl: string;
  };
  // Campos para suporte a parcelamento de boleto
  is_boleto_installment?: boolean;
  boleto_details?: Array<{
    amount: number;
    asaas_payment_id: string;
    due_date: string;
    installment_number: number;
    payment_url: string;
    status: string;
    transaction_id: string;
  }>;
  checkout: Checkout;
}

export function PaymentStatusPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [includePixQrCode, setIncludePixQrCode] = useState(true);

  const paymentId = searchParams.get('payment');

  let interval: ReturnType<typeof setInterval> | null = null;

  const fetchPaymentData = async () => {
    try {
      const response = await checkoutService.getCheckout(
        paymentId || '',
        includePixQrCode
      );

      if (!response.success || !response.data) {
        throw new Error(
          response.error?.message || 'Erro ao buscar dados do pagamento'
        );
      }

      setPaymentData(response.data);

      // Atualizar dados do usuário no Sentry com informações do pagamento
      if (response.data?.transaction?.contacts) {
        const contact = response.data.transaction.contacts;
        setSentryUser({
          id: contact.email || contact.id,
          name: contact.name || undefined,
          email: contact.email || undefined,
          phone: contact.phone || undefined,
          username: contact.name || undefined,
          segment: 'payment-status-customer',
          additionalData: {
            transactionId: response.data.transaction.id,
            paymentMethod: response.data.transaction.payment_method,
            paymentStatus: response.data.asaas_payment?.status,
            billingType: response.data.billing_type,
            amount: response.data.transaction.amount,
            formStep: 'payment-status-page',
            hasDocument: !!contact.document,
          },
        });

        // Adicionar contexto adicional sobre o pagamento
        updateSentryUserContext({
          paymentDetails: {
            status: response.data.asaas_payment?.status,
            billingType: response.data.billing_type,
            amount: response.data.transaction.amount,
            dueDate: response.data.transaction.due_date,
            paymentDate: response.data.transaction.payment_date,
            installments: response.data.transaction.installments,
          },
        });
      }

      if (
        response.data.billing_type === 'PIX' &&
        !interval &&
        response.data.asaas_payment.status === 'PENDING'
      ) {
        interval = setInterval(() => {
          fetchPaymentData();
        }, 5000);
      } else if (interval) {
        clearInterval(interval);
        interval = null;
      }

      // Para PIX, calcular tempo restante se houver data de vencimento
      if (response.data.billing_type === 'PIX') {
        const expirationTime =
          new Date(response.data.transaction.created_at).getTime() +
          (response.data.checkout.pix_expiration_time_minutes || 20) *
            60 *
            1000; // adiciona 10 minutos
        const now = Date.now();
        const timeRemaining = Math.max(
          0,
          Math.floor((expirationTime - now) / 1000)
        );
        setTimeLeft(timeRemaining);
      }
    } catch (err) {
      logger.error('Erro ao buscar dados do pagamento:', err);
      if (err instanceof CheckoutApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro inesperado ao carregar dados do pagamento');
      }
    } finally {
      setIsLoading(false);
      setIncludePixQrCode(false);
    }
  };

  useEffect(() => {
    if (!paymentId) {
      setError('ID do pagamento não encontrado');
      setIsLoading(false);
      return;
    }

    fetchPaymentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  // Timer para PIX
  useEffect(() => {
    if (paymentData?.billing_type === 'PIX' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => Math.max(0, prev - 1));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [paymentData?.billing_type, timeLeft]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const [copySuccess, setCopySuccess] = useState<string>('');

  const copyToClipboard = async (text: string, type: string = '') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type || 'Copiado!');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      logger.error('Erro ao copiar para clipboard:', err);
      setCopySuccess('Erro ao copiar');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  // Componente do card do produto
  const ProductCard = () => (
    <div className="mb-8 overflow-hidden rounded-2xl bg-white shadow-lg">
      <div className="p-4">
        <div className="flex items-start space-x-4">
          <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl">
            <img
              src={
                paymentData?.checkout.attachments?.find(
                  attachment => attachment.attachment_type === 'square'
                )?.url
              }
              alt={
                paymentData?.checkout.attachments?.find(
                  attachment => attachment.attachment_type === 'square'
                )?.filename
              }
              className="h-full w-full object-cover"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {paymentData?.checkout.name}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm text-gray-500">
              {paymentData?.checkout.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-blue-100">
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-gray-800">
                Carregando dados do pagamento
              </h2>
              <p className="text-gray-600">Aguarde um momento...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !paymentData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-100 p-4">
        <div className="flex min-h-screen items-center justify-center">
          <div className="mx-auto max-w-lg rounded-2xl bg-white p-8 text-center shadow-xl">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-10 w-10 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-900">
              Ops! Algo deu errado
            </h2>
            <p className="mb-6 text-gray-600">{error}</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Voltar para o Checkout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar baseado no tipo de pagamento
  const renderPaymentContent = () => {
    switch (paymentData.billing_type) {
      case 'CREDIT_CARD':
        return renderCreditCardSuccess();
      case 'PIX':
        return renderPixPayment();
      case 'BOLETO':
        return renderBoletoPayment();
      default:
        return renderGenericSuccess();
    }
  };

  const renderCreditCardSuccess = () => (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-100 p-4">
      <div className="mx-auto max-w-2xl py-8">
        {/* Header de Sucesso */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-green-100 shadow-lg">
            <svg
              className="h-12 w-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="mb-4 text-4xl font-bold text-gray-900">
            Pagamento Confirmado!
          </h1>
          <p className="text-xl text-gray-600">
            Seu pedido foi processado com sucesso
          </p>
        </div>

        {/* Card do Produto */}
        <ProductCard />

        {/* Informações do Pagamento */}
        <div className="mb-8 overflow-hidden rounded-2xl bg-white shadow-lg">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4">
            <h2 className="text-xl font-semibold text-white">
              Detalhes do Pagamento
            </h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Valor Pago</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(paymentData.asaas_payment.value)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">
                  Parcelamento
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {paymentData.asaas_payment.installmentCount || '1'} x{' '}
                  {formatCurrency(paymentData.asaas_payment.paymentValue)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Status</p>
                <div className="mt-2 inline-flex items-center rounded-full bg-green-100 px-3 py-1">
                  <div className="mr-2 h-2 w-2 rounded-full bg-green-400"></div>
                  <span className="text-sm font-medium text-green-800">
                    Aprovado
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Próximos Passos */}
        <div className="mb-8 overflow-hidden rounded-2xl bg-white shadow-lg">
          <div className="p-4">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Próximos Passos
            </h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <span className="text-xs font-bold text-blue-600">1</span>
                </div>
                <p className="ml-3 text-sm text-gray-700">
                  Você receberá um e-mail de confirmação em instantes
                </p>
              </div>
              <div className="flex items-center">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <span className="text-xs font-bold text-blue-600">2</span>
                </div>
                <p className="ml-3 text-sm text-gray-700">
                  Suas informações de acesso serão enviadas em até 24h
                </p>
              </div>
              <div className="flex items-center">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <span className="text-xs font-bold text-blue-600">3</span>
                </div>
                <p className="ml-3 text-sm text-gray-700">
                  Prepare-se para uma jornada incrível de aprendizado!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPixPayment = () => {
    // Se o PIX foi pago (RECEIVED), mostrar página de sucesso verde
    if (paymentData.asaas_payment.status === 'RECEIVED') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-100 p-4">
          <div className="mx-auto max-w-2xl py-8">
            {/* Header de Sucesso PIX */}
            <div className="mb-8 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-green-100 shadow-lg">
                <svg
                  className="h-12 w-12 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="mb-4 text-4xl font-bold text-gray-900">
                PIX Recebido!
              </h1>
              <p className="text-xl text-gray-600">
                Seu pagamento foi processado com sucesso
              </p>
            </div>

            {/* Card do Produto */}
            <ProductCard />

            {/* Informações do Pagamento PIX */}
            <div className="mb-8 overflow-hidden rounded-2xl bg-white shadow-lg">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4">
                <h2 className="text-xl font-semibold text-white">
                  Detalhes do Pagamento
                </h2>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">
                      Valor Pago
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(paymentData.asaas_payment.value)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Método</p>
                    <div className="mt-2 inline-flex items-center rounded-full bg-green-100 px-3 py-1">
                      <div className="mr-2 h-2 w-2 rounded-full bg-green-400"></div>
                      <span className="text-sm font-medium text-green-800">
                        PIX - Aprovado
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Próximos Passos */}
            <div className="mb-8 overflow-hidden rounded-2xl bg-white shadow-lg">
              <div className="p-4">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Próximos Passos
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-xs font-bold text-blue-600">1</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      Você receberá um e-mail de confirmação em instantes
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-xs font-bold text-blue-600">2</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      Suas informações de acesso serão enviadas em até 24h
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-xs font-bold text-blue-600">3</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      Prepare-se para uma jornada incrível de aprendizado!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Se ainda está pendente, mostrar QR Code
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 p-4">
        <div className="mx-auto max-w-2xl py-8">
          {/* Header PIX */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-blue-100 shadow-lg">
              <svg
                className="h-12 w-12 text-blue-600"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
            <h1 className="mb-4 text-4xl font-bold text-gray-900">
              Pagamento PIX
            </h1>
            <p className="text-xl text-gray-600">
              Escaneie o QR Code ou copie o código
            </p>

            {timeLeft > 0 && (
              <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-100 p-4 backdrop-blur-md">
                <p className="text-sm text-gray-900">
                  Tempo restante para pagamento
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {formatTime(timeLeft)}
                </p>
              </div>
            )}
            {timeLeft === 0 && (
              <div className="mt-6 rounded-2xl border border-red-400/20 bg-red-500/20 p-4 backdrop-blur-md">
                <p className="text-red-100">
                  QR Code expirado. Refaça o pedido.
                </p>
              </div>
            )}
          </div>

          {/* Card do Produto */}
          <div className="mb-8">
            <ProductCard />
          </div>

          {/* QR Code e Informações */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
              <h2 className="text-xl font-semibold text-white">
                Dados para Pagamento
              </h2>
            </div>

            <div className="p-8">
              <div className="mb-8 text-center">
                <div className="mx-auto mb-6 h-64 w-64 overflow-hidden rounded-2xl bg-gray-100 shadow-lg">
                  {paymentData.pix_qr_code ? (
                    <img
                      src={`data:image/png;base64,${paymentData.pix_qr_code.encodedImage}`}
                      alt="QR Code PIX"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-gray-400">
                      <div className="text-center">
                        <svg
                          className="mx-auto mb-4 h-16 w-16"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h2v2H7V7zm4 0h2v2h-2V7zm4 0h2v2h-2V7zM7 11h2v2H7v-2zm8 0h2v2h-2v-2zM7 15h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z" />
                        </svg>
                        <p className="text-sm">
                          QR Code será gerado em instantes
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-6 text-center">
                  <p className="text-sm font-medium text-gray-600">
                    Valor a Pagar
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(paymentData.asaas_payment.value)}
                  </p>
                </div>
              </div>

              {paymentData.pix_qr_code && (
                <div className="mb-8">
                  <label className="mb-3 block text-sm font-medium text-gray-600">
                    Código PIX - Copia e Cola
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 rounded-xl border border-gray-300 bg-gray-50 p-4">
                      <code className="break-all font-mono text-sm leading-relaxed text-gray-800">
                        {paymentData.pix_qr_code.payload}
                      </code>
                    </div>
                    <Button
                      onClick={() =>
                        copyToClipboard(
                          paymentData.pix_qr_code.payload || '',
                          'Código PIX copiado!'
                        )
                      }
                      className="bg-blue-600 hover:bg-blue-700"
                      size="lg"
                    >
                      {copySuccess === 'Código PIX copiado!' ? (
                        <svg
                          className="h-5 w-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </Button>
                  </div>
                  {copySuccess === 'Código PIX copiado!' && (
                    <p className="mt-2 animate-fade-in-up text-sm text-green-600">
                      {copySuccess}
                    </p>
                  )}
                </div>
              )}

              <div className="rounded-xl bg-blue-50 p-4">
                <h4 className="mb-4 font-semibold text-blue-900">
                  Como pagar com PIX:
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex items-start space-x-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      1
                    </div>
                    <p className="text-sm text-blue-900">
                      Abra seu banco ou carteira digital
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      2
                    </div>
                    <p className="text-sm text-blue-900">
                      Escolha "Pagar com PIX"
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      3
                    </div>
                    <p className="text-sm text-blue-900">
                      Escaneie o QR Code ou cole o código
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                      4
                    </div>
                    <p className="text-sm text-blue-900">
                      Confirme o pagamento
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-lg bg-blue-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">
                      Aprovação instantânea
                    </h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Após o pagamento, você será redirecionado automaticamente!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ações */}
          <div className="mt-8 text-center">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="lg"
              className="border-blue-600 bg-blue-100 px-8 py-4 text-blue-600 backdrop-blur-md hover:bg-blue-200"
            >
              Voltar para o Checkout
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Componente auxiliar para renderizar um boleto individual
  const renderSingleBoleto = (
    boletoData: {
      amount: number;
      due_date: string;
      payment_url: string;
      installment_number?: number;
    },
    boletoInfo?: {
      digitableLine: string;
      barcode: string;
      bankSlipUrl: string;
    }
  ) => (
    <div className="mb-6 overflow-hidden rounded-2xl bg-white shadow-lg">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
        <h2 className="text-xl font-semibold text-white">
          {boletoData.installment_number
            ? `Boleto ${boletoData.installment_number}ª Parcela`
            : 'Informações do Boleto'}
        </h2>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600">Valor a Pagar</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(boletoData.amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600">Vencimento</p>
            <p className="text-xl font-bold text-gray-900">
              {format(
                setHours(new Date(boletoData.due_date), 24),
                'dd/MM/yyyy'
              )}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-center">
            <Button
              onClick={() => window.open(boletoData.payment_url, '_blank')}
              className="bg-blue-600 hover:bg-blue-700"
              size="md"
            >
              <svg
                className="mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              Visualizar Boleto
            </Button>
          </div>
        </div>

        {/* Código de barras - só mostra se tiver boletoInfo (boleto único) */}
        {boletoInfo && (
          <div className="mt-6">
            <h4 className="mb-4 text-lg font-semibold text-gray-900">
              Código de Barras
            </h4>
            <div className="flex items-center space-x-2">
              <div className="flex-1 rounded-lg border border-gray-300 bg-gray-50 p-3">
                <code className="break-all font-mono text-sm text-gray-800">
                  {boletoInfo.barcode}
                </code>
              </div>
              <Button
                onClick={() =>
                  copyToClipboard(
                    boletoInfo.barcode,
                    'Código de barras copiado!'
                  )
                }
                variant="outline"
                size="sm"
              >
                {copySuccess === 'Código de barras copiado!' ? (
                  <svg
                    className="h-4 w-4 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 002 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </Button>
            </div>
            {copySuccess && (
              <p className="mt-2 animate-fade-in-up text-sm text-green-600">
                {copySuccess}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderBoletoPayment = () => {
    // Verificar se é parcelamento de boleto
    const isBoletoInstallment = paymentData.is_boleto_installment;
    const boletoDetails = paymentData.boleto_details;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 p-4">
        <div className="mx-auto max-w-2xl py-8">
          {/* Header do Boleto */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-blue-100 shadow-lg">
              <svg
                className="h-12 w-12 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 className="mb-4 text-4xl font-bold text-gray-900">
              {isBoletoInstallment && boletoDetails && boletoDetails.length > 1
                ? `${boletoDetails.length} Boletos Gerados!`
                : 'Boleto Gerado!'}
            </h1>
            <p className="text-xl text-gray-600">
              {isBoletoInstallment && boletoDetails && boletoDetails.length > 1
                ? 'Seus boletos estão prontos para pagamento'
                : 'Seu pedido está quase pronto'}
            </p>
          </div>

          {/* Card do Produto */}
          <ProductCard />

          {/* Renderizar boletos */}
          {isBoletoInstallment && boletoDetails && boletoDetails.length > 0 ? (
            // Múltiplos boletos - parcelamento
            <>
              {/* Resumo do parcelamento */}
              <div className="mb-6 overflow-hidden rounded-2xl bg-white shadow-lg">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4">
                  <h2 className="text-xl font-semibold text-white">
                    Resumo do Parcelamento
                  </h2>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">
                        Total de Parcelas
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {boletoDetails.length}x
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">
                        Valor Total
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(
                          boletoDetails.reduce(
                            (sum, boleto) => sum + boleto.amount,
                            0
                          )
                        )}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600">
                        Primeira Parcela
                      </p>
                      <p className="text-xl font-bold text-gray-900">
                        {format(
                          setHours(new Date(boletoDetails[0].due_date), 24),
                          'dd/MM/yyyy'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lista de boletos */}
              {boletoDetails
                .sort((a, b) => a.installment_number - b.installment_number)
                .map(boleto => (
                  <div key={boleto.asaas_payment_id || boleto.transaction_id}>
                    {renderSingleBoleto({
                      amount: boleto.amount,
                      due_date: boleto.due_date,
                      payment_url: boleto.payment_url,
                      installment_number: boleto.installment_number,
                    })}
                  </div>
                ))}
            </>
          ) : (
            // Boleto único - comportamento original
            renderSingleBoleto(
              {
                amount: paymentData.asaas_payment.value,
                due_date: paymentData.asaas_payment.dueDate,
                payment_url: paymentData.asaas_payment?.bankSlipUrl,
              },
              paymentData.boleto_info
            )
          )}

          {/* Instruções */}
          <div className="mb-8 overflow-hidden rounded-2xl bg-white shadow-lg">
            <div className="p-4">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Como pagar
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-xs font-bold text-blue-600">1</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      {isBoletoInstallment &&
                      boletoDetails &&
                      boletoDetails.length > 1
                        ? 'Baixe cada boleto ou copie as linhas digitáveis'
                        : 'Baixe o boleto ou copie a linha digitável'}
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-xs font-bold text-blue-600">2</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      Acesse o internet banking ou caixa eletrônico
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-xs font-bold text-blue-600">3</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      {isBoletoInstallment &&
                      boletoDetails &&
                      boletoDetails.length > 1
                        ? 'Pague cada boleto até sua data de vencimento'
                        : 'Efetue o pagamento até a data de vencimento'}
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <span className="text-xs font-bold text-blue-600">4</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      Aguarde até 2 dias úteis para confirmação
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-blue-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">
                      Importante
                    </h4>
                    <p className="mt-1 text-sm text-blue-700">
                      {isBoletoInstallment &&
                      boletoDetails &&
                      boletoDetails.length > 1
                        ? 'Após o vencimento, cada boleto não poderá ser pago. Mantenha-se atento às datas de cada parcela.'
                        : 'Após o vencimento, o boleto não poderá ser pago. Caso isso aconteça, entre em contato conosco.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGenericSuccess = () => (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50 via-white to-emerald-50 p-4">
      <div className="mx-auto max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <svg
            className="h-8 w-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h1 className="mb-4 text-2xl font-bold text-gray-900">
          Pedido Processado!
        </h1>

        <div className="mb-6 rounded-lg bg-gray-50 p-4">
          <p className="text-sm text-gray-600">Status</p>
          <p className="text-lg font-bold text-gray-900">
            {paymentData.asaas_payment.status}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            ID: {paymentData.asaas_payment.id}
          </p>
        </div>
      </div>
    </div>
  );

  return renderPaymentContent();
}
